import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { FilterableSelect, type FilterableSelectOption } from "@/components/ui/filterable-select"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Lock, Unlock, AlertTriangle } from "lucide-react"
import type { DealName, AccAssignment } from "@/lib/api"
import {
  type AnalyticPartDefinition,
  type AnalyticPartFieldDef,
  type AnalyticPartOption,
  type AnalyticPartsCache,
} from "../../hooks/useErpOptions"

interface LineItemErpMetadata {
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
  without_account_assignment?: boolean
}

interface LineItemErpRowProps {
  lineItemGroup: string
  clientId?: string
  erpMetadata?: LineItemErpMetadata
  onMetadataChange: (lineItemGroup: string, metadata: LineItemErpMetadata) => void
  disabled?: boolean
  dealNames: DealName[]
  isLoadingDealNames: boolean
  accAssignments: AccAssignment[]
  isLoadingAccAssignments: boolean
  analyticPartsCache: AnalyticPartsCache
  withoutAccountAssignment?: boolean
}

export const LineItemErpRow = ({
  lineItemGroup,
  clientId,
  erpMetadata,
  onMetadataChange,
  disabled = false,
  dealNames,
  isLoadingDealNames,
  accAssignments,
  isLoadingAccAssignments,
  analyticPartsCache,
  withoutAccountAssignment = false
}: LineItemErpRowProps): JSX.Element => {

  // Analytic Parts state
  const [analyticPartSelections, setAnalyticPartSelections] = useState<Record<string, string>>({})

  // Local state for immediate UI feedback
  const [selectedDealName, setSelectedDealName] = useState<number | undefined>(erpMetadata?.deal_name)
  const [selectedAccAssignment, setSelectedAccAssignment] = useState<number | undefined>(erpMetadata?.acc_assignment)

  // Update local state when erpMetadata prop changes
  useEffect(() => {
    setSelectedDealName(erpMetadata?.deal_name)
    setSelectedAccAssignment(erpMetadata?.acc_assignment)
  }, [erpMetadata?.deal_name, erpMetadata?.acc_assignment])

  // Fetch analytic parts via shared cache (deduplicates across rows)
  useEffect(() => {
    analyticPartsCache.getOrFetch(selectedAccAssignment)
  }, [analyticPartsCache, selectedAccAssignment])

  const analyticPartDefinitions = analyticPartsCache.get(selectedAccAssignment)
  const isLoadingAnalyticParts = analyticPartsCache.isLoading(selectedAccAssignment)

  // Lock/unlock state for cascading filtered dropdowns (session-only)
  const [isLocked, setIsLocked] = useState(true)

  // Whether any definition has multi-level fields with combinations (cascading makes sense)
  const hasCombinations = useMemo(() =>
    analyticPartDefinitions.some((d) => {
      if (!d.combinations?.length) return false
      const fields = d.fields ?? []
      const lookupFields = fields.filter((f) => f.lookup_nomen_type_fk > 0)
      return lookupFields.length > 1
    }),
    [analyticPartDefinitions]
  )

  // Build selection key — includes rowNum for per-field tracking
  const buildSelectionKey = (accountCode: string, analyticPartId: number, rowNum: number) =>
    `${accountCode}-${analyticPartId}-${rowNum}`

  // Get effective fields for a definition (backward compat if fields absent)
  const getEffectiveFields = (definition: AnalyticPartDefinition): AnalyticPartFieldDef[] => {
    if (definition.fields && definition.fields.length > 0) {
      return definition.fields
    }
    // Backward compat: synthesize a single field from top-level options
    return [{
      field_id: 0,
      field_name: "",
      label: definition.account_name,
      row_num: 1,
      lookup_nomen_type_fk: definition.lookup_nomen_type_fk,
      options: definition.options,
      all_options: [],
    }]
  }

  // Get filtered options for a field based on lock state and upstream selections
  const getFilteredFieldOptions = useCallback((
    definition: AnalyticPartDefinition,
    field: AnalyticPartFieldDef,
  ): AnalyticPartOption[] => {
    const defCombinations = definition.combinations ?? []

    if (!isLocked || defCombinations.length === 0) {
      // Unlocked or no combinations: use all_options if available, fall back to options
      return field.all_options?.length ? field.all_options : field.options
    }

    // Locked mode: filter combinations by upstream level selections
    let filteredCombos = defCombinations

    for (let r = 1; r < field.row_num; r++) {
      const upstreamKey = buildSelectionKey(definition.account_code, definition.analytic_part_id, r)
      const upstreamValue = analyticPartSelections[upstreamKey]
      if (upstreamValue) {
        filteredCombos = filteredCombos.filter((c) =>
          c.levels.find((l) => l.row_num === r)?.code === upstreamValue
        )
      }
    }

    // Extract unique values for this level from filtered combinations
    const validValues = new Set(
      filteredCombos
        .map((c) => c.levels.find((l) => l.row_num === field.row_num)?.code)
        .filter(Boolean)
    )

    return field.options.filter((opt) => validValues.has(opt.value))
  }, [isLocked, analyticPartSelections])

  // Check if current selections form a valid combination for a definition
  const isValidCombination = useCallback((definition: AnalyticPartDefinition): boolean | null => {
    const defCombinations = definition.combinations ?? []
    if (defCombinations.length === 0) return null // no combinations to validate against

    const fields = getEffectiveFields(definition)
    const selectedValues: Record<number, string> = {}
    let hasAnySelection = false

    for (const field of fields) {
      const key = buildSelectionKey(definition.account_code, definition.analytic_part_id, field.row_num)
      const val = analyticPartSelections[key]
      if (val) {
        selectedValues[field.row_num] = val
        hasAnySelection = true
      }
    }

    if (!hasAnySelection) return null // nothing selected yet

    // Check if there's a combination that matches all selected values
    return defCombinations.some((combo) =>
      Object.entries(selectedValues).every(([rowNumStr, code]) => {
        const rowNum = Number(rowNumStr)
        return combo.levels.find((l) => l.row_num === rowNum)?.code === code
      })
    )
  }, [analyticPartSelections])

  // Current analytic parts from metadata
  const currentAnalyticParts = useMemo(() => {
    if (Array.isArray(erpMetadata?.analytic_parts)) {
      return erpMetadata.analytic_parts
    }
    return []
  }, [erpMetadata?.analytic_parts])

  // Sync analytic part selections with definitions and current values
  useEffect(() => {
    if (analyticPartDefinitions.length === 0) {
      setAnalyticPartSelections({})
      return
    }

    // Don't sync if local state doesn't match props - we're mid-change
    if (selectedAccAssignment !== erpMetadata?.acc_assignment) {
      return
    }

    const nextSelections: Record<string, string> = {}

    analyticPartDefinitions.forEach((definition) => {
      const existing = currentAnalyticParts.find((part) => part.acc === definition.account_code)
      if (!existing) return

      const fields = getEffectiveFields(definition)
      fields.forEach((field) => {
        const key = buildSelectionKey(definition.account_code, definition.analytic_part_id, field.row_num)
        const levelKey = `level${field.row_num}` as keyof typeof existing
        const levelNameKey = `level${field.row_num}_name` as keyof typeof existing
        const levelValue = existing[levelKey] as string | undefined
        const levelName = existing[levelNameKey] as string | undefined

        let resolvedValue: string | undefined
        const allOpts = [...field.options, ...(field.all_options ?? [])]

        if (levelValue) {
          const match = allOpts.find((opt) => opt.value === levelValue)
          if (match) resolvedValue = match.value
        }

        // Fallback: match by name
        if (!resolvedValue && levelName) {
          const nameMatch = allOpts.find(
            (opt) => opt.label.toLowerCase() === levelName.toLowerCase()
          )
          if (nameMatch) resolvedValue = nameMatch.value
        }

        if (resolvedValue) {
          nextSelections[key] = resolvedValue
        }
      })
    })

    setAnalyticPartSelections(nextSelections)
  }, [analyticPartDefinitions, currentAnalyticParts, selectedAccAssignment, erpMetadata?.acc_assignment])

  // Auto-select when filtering leaves only one option (locked mode)
  const autoSelectRef = useRef(false)
  useEffect(() => {
    if (!isLocked || !hasCombinations || analyticPartDefinitions.length === 0) return
    // Avoid re-entry
    if (autoSelectRef.current) return
    autoSelectRef.current = true

    let changed = false
    const updatedSelections = { ...analyticPartSelections }

    analyticPartDefinitions.forEach((definition) => {
      if (!definition.combinations?.length) return
      const fields = getEffectiveFields(definition)

      fields.forEach((field) => {
        const key = buildSelectionKey(definition.account_code, definition.analytic_part_id, field.row_num)
        if (updatedSelections[key]) return // already selected

        const filtered = getFilteredFieldOptions(definition, field)
        if (filtered.length === 1) {
          updatedSelections[key] = filtered[0].value
          changed = true
        }
      })
    })

    if (changed) {
      setAnalyticPartSelections(updatedSelections)
    }

    // Use setTimeout to reset ref after this render cycle
    setTimeout(() => { autoSelectRef.current = false }, 0)
  }, [isLocked, hasCombinations, analyticPartDefinitions, analyticPartSelections, getFilteredFieldOptions])

  // Handle without account assignment toggle
  const handleWithoutAccToggle = useCallback((checked: boolean) => {
    // checked=true means "include acc", so without=false
    const withoutAcc = !checked
    onMetadataChange(lineItemGroup, {
      deal_name: selectedDealName,
      acc_assignment: selectedAccAssignment,
      analytic_parts: currentAnalyticParts,
      without_account_assignment: withoutAcc,
    })
  }, [lineItemGroup, selectedDealName, selectedAccAssignment, currentAnalyticParts, onMetadataChange])

  // Handle deal name change
  const handleDealNameChange = useCallback((value: string) => {
    const numericValue = parseInt(value, 10)
    if (isNaN(numericValue)) return

    setSelectedDealName(numericValue)
    onMetadataChange(lineItemGroup, {
      deal_name: numericValue,
      acc_assignment: selectedAccAssignment,
      analytic_parts: currentAnalyticParts,
      without_account_assignment: withoutAccountAssignment,
    })
  }, [lineItemGroup, selectedAccAssignment, currentAnalyticParts, withoutAccountAssignment, onMetadataChange])

  // Handle account assignment change
  const handleAccAssignmentChange = useCallback((value: string) => {
    const numericValue = parseInt(value, 10)
    if (isNaN(numericValue)) return

    // Clear local analytic part selections when account assignment changes
    setAnalyticPartSelections({})

    setSelectedAccAssignment(numericValue)

    // Use local state for deal_name, reset analytic parts for new account
    onMetadataChange(lineItemGroup, {
      deal_name: selectedDealName,
      acc_assignment: numericValue,
      analytic_parts: [],
      without_account_assignment: withoutAccountAssignment,
    })
  }, [lineItemGroup, selectedDealName, withoutAccountAssignment, onMetadataChange])

  // Handle analytic part field selection change
  const handleAnalyticSelectionChange = useCallback((
    accountCode: string,
    analyticPartId: number,
    rowNum: number,
    newValue: string
  ) => {
    const key = buildSelectionKey(accountCode, analyticPartId, rowNum)
    const updatedSelections = {
      ...analyticPartSelections,
      [key]: newValue
    }

    // Clear downstream levels (rowNum+1, rowNum+2, etc.)
    const definition = analyticPartDefinitions.find(
      (d) => d.account_code === accountCode && d.analytic_part_id === analyticPartId
    )
    if (definition) {
      const fields = getEffectiveFields(definition)
      fields.forEach((f) => {
        if (f.row_num > rowNum) {
          const downstreamKey = buildSelectionKey(accountCode, analyticPartId, f.row_num)
          delete updatedSelections[downstreamKey]
        }
      })
    }

    setAnalyticPartSelections(updatedSelections)

    // Build analytic parts payload — one entry per account with level1/level2/level3
    const analyticPartsPayload = analyticPartDefinitions.map((def) => {
      const fields = getEffectiveFields(def)
      const entry: Record<string, string | undefined> = { acc: def.account_code }
      let hasAnyValue = false

      fields.forEach((field) => {
        const selKey = buildSelectionKey(def.account_code, def.analytic_part_id, field.row_num)
        const selectedValue = updatedSelections[selKey]
        // Look up label from options or all_options
        const allOpts = [...field.options, ...(field.all_options ?? [])]
        const selectedOption = allOpts.find((opt) => opt.value === selectedValue)
        const selectedLabel = selectedOption?.label?.trim()

        if (selectedValue) {
          entry[`level${field.row_num}`] = selectedValue
          entry[`level${field.row_num}_name`] = selectedLabel || selectedValue
          hasAnyValue = true
        }
      })

      return hasAnyValue ? entry : null
    }).filter(Boolean)

    onMetadataChange(lineItemGroup, {
      deal_name: selectedDealName,
      acc_assignment: selectedAccAssignment,
      analytic_parts: analyticPartsPayload as LineItemErpMetadata["analytic_parts"],
      without_account_assignment: withoutAccountAssignment,
    })
  }, [lineItemGroup, selectedDealName, selectedAccAssignment, withoutAccountAssignment, onMetadataChange, analyticPartSelections, analyticPartDefinitions])


  // Convert deal names to FilterableSelectOption format
  const dealNameOptions: FilterableSelectOption[] = useMemo(() => {
    return dealNames.map((dn) => ({
      value: dn.id.toString(),
      label: dn.description,
      searchText: `${dn.vat_rate}% ${dn.id} ${dn.description}`
    }))
  }, [dealNames])

  // Convert account assignments to FilterableSelectOption format (sorted by code)
  const accAssignmentOptions: FilterableSelectOption[] = useMemo(() => {
    return [...accAssignments]
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      .map((aa) => ({
        value: aa.id.toString(),
        label: aa.description,
        description: aa.code || undefined,
        searchText: `${aa.code || ''} ${aa.description}`
      }))
  }, [accAssignments])

  // Convert field options to FilterableSelectOption format
  const getFieldOptions = (options: AnalyticPartOption[]): FilterableSelectOption[] => {
    return [...options]
      .sort((a, b) => Number(a.value) - Number(b.value))
      .map((opt) => ({
        value: opt.value,
        label: opt.label,
        description: opt.apx_id != null ? `${opt.apx_id} · ${opt.value}` : opt.value,
        searchText: `${opt.apx_id ?? ''} ${opt.value} ${opt.label}`
      }))
  }

  // Render a single field dropdown within an analytic part group
  const renderFieldDropdown = (
    definition: AnalyticPartDefinition,
    field: AnalyticPartFieldDef,
    showAccountCode = false,
    isInvalidCombo = false,
  ) => {
    const key = buildSelectionKey(definition.account_code, definition.analytic_part_id, field.row_num)
    const selectedValue = analyticPartSelections[key] || ""
    const fieldLabel = field.label || `Level ${field.row_num}`
    const displayLabel = showAccountCode ? `${definition.account_code} ${fieldLabel}` : fieldLabel

    const filteredOptions = getFilteredFieldOptions(definition, field)

    return (
      <div key={key} className="flex flex-col gap-0.5">
        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
          {displayLabel}
        </span>
        <FilterableSelect
          value={selectedValue}
          onValueChange={(value) => handleAnalyticSelectionChange(
            definition.account_code,
            definition.analytic_part_id,
            field.row_num,
            value
          )}
          disabled={disabled || filteredOptions.length === 0 || withoutAccountAssignment}
          placeholder={`Select ${field.label || definition.account_code}`}
          searchPlaceholder={`Search ${field.label || "analytic parts"}...`}
          options={getFieldOptions(filteredOptions)}
          triggerClassName={cn(
            "h-8 text-sm border-gray-200 bg-white",
            isInvalidCombo && selectedValue && "ring-2 ring-amber-300"
          )}
          renderSelectedValue={(opt) => (
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[10px] bg-violet-100 text-violet-700 px-1 py-0.5 rounded shrink-0">
                {opt.value}
              </span>
              <span className="truncate">{opt.label}</span>
            </div>
          )}
          renderOption={(opt) => (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                {opt.value}
              </span>
              <span>{opt.label}</span>
            </div>
          )}
        />
      </div>
    )
  }

  // Render an analytic part group (account with 1-3 field dropdowns)
  const renderAnalyticPartGroup = (definition: AnalyticPartDefinition) => {
    const fields = getEffectiveFields(definition)
    const hasMultipleFields = fields.length > 1
    const groupKey = `${definition.account_code}-${definition.analytic_part_id}`
    const validity = isValidCombination(definition)
    const isInvalidCombo = validity === false
    const defHasMultiLevelCombos = (definition.combinations?.length ?? 0) > 0
      && fields.filter((f) => f.lookup_nomen_type_fk > 0).length > 1
    const showLockedBorder = isLocked && defHasMultiLevelCombos

    return (
      <Tooltip key={groupKey}>
        <TooltipTrigger asChild>
          <div className={cn(
            "flex flex-col gap-1",
            showLockedBorder && "border-l-2 border-violet-200 pl-2"
          )}>
            {hasMultipleFields ? (
              <>
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  {definition.account_code} — {definition.account_name}
                </span>
                <div className="flex flex-col gap-1.5 pl-0">
                  {fields.map((field) => renderFieldDropdown(definition, field, false, isInvalidCombo))}
                </div>
              </>
            ) : (
              renderFieldDropdown(definition, fields[0], true, isInvalidCombo)
            )}
            {isInvalidCombo && (
              <div className="flex items-center gap-1 mt-0.5">
                <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                <span className="text-[10px] text-amber-600">
                  Комбинацията не съществува в Мираж — ще бъде създадена при импорт
                </span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="font-medium">{definition.account_code} - {definition.account_name}</p>
          {definition.directions.length > 0 && (
            <p className="text-[10px] opacity-70">Roles: {definition.directions.join(", ")}</p>
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div className={cn(
        "gap-x-3 gap-y-2 pl-7 py-2.5 bg-gray-50/50 border-t border-dashed border-neutral-200",
        withoutAccountAssignment ? "flex flex-col" : "grid grid-cols-2"
      )}>
        {/* Per-row include account assignment toggle */}
        {!disabled && (
          <div className={cn("flex items-center gap-2", !withoutAccountAssignment && "col-span-2")}>
            <Switch
              checked={!withoutAccountAssignment}
              onCheckedChange={handleWithoutAccToggle}
              aria-label="Include account assignment"
              className="h-4 w-7 data-[state=checked]:bg-blue-200 data-[state=unchecked]:bg-neutral-300 [&>span]:data-[state=checked]:bg-[#0000FF] [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3"
            />
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Acc. assignment</span>
          </div>
        )}

        {/* Deal Name — always visible */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Deal Name</span>
          <FilterableSelect
            value={selectedDealName?.toString() || ""}
            onValueChange={handleDealNameChange}
            disabled={disabled || isLoadingDealNames || !clientId}
            loading={isLoadingDealNames}
            placeholder="Select deal name"
            searchPlaceholder="Search deals..."
            options={dealNameOptions}
            triggerClassName="h-9 text-sm border-gray-200 bg-white"
            renderSelectedValue={(opt) => {
              const dn = dealNames.find((d) => d.id.toString() === opt.value)
              return dn ? (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded shrink-0">{dn.id}</span>
                  <span className="truncate">{dn.description}</span>
                </div>
              ) : null
            }}
            renderOption={(opt) => {
              const dn = dealNames.find((d) => d.id.toString() === opt.value)
              return dn ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] bg-emerald-100 text-emerald-700 min-w-[24px] text-center py-0.5 rounded">
                    {dn.id}
                  </span>
                  <span className="text-[10px] bg-gray-100 text-gray-600 min-w-[24px] text-center py-0.5 rounded">
                    {dn.vat_rate}%
                  </span>
                  <span className="text-sm text-gray-600 truncate">
                    {dn.description}
                  </span>
                </div>
              ) : opt.label
            }}
          />
        </div>

        {!withoutAccountAssignment && (
          <>
            {/* Account Assignment */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Account Assignment</span>
              <FilterableSelect
                value={selectedAccAssignment?.toString() || ""}
                onValueChange={handleAccAssignmentChange}
                disabled={disabled || isLoadingAccAssignments || !clientId}
                loading={isLoadingAccAssignments}
                placeholder="Select account"
                searchPlaceholder="Search accounts..."
                options={accAssignmentOptions}
                triggerClassName="h-9 text-sm border-gray-200 bg-white"
                renderSelectedValue={(opt) => {
                  const aa = accAssignments.find((a) => a.id.toString() === opt.value)
                  return aa ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      {aa.code && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded shrink-0">
                          {aa.code}
                        </span>
                      )}
                      <span className="truncate">{aa.description}</span>
                    </div>
                  ) : null
                }}
                renderOption={(opt) => {
                  const aa = accAssignments.find((a) => a.id.toString() === opt.value)
                  return aa ? (
                    <div className="flex flex-col items-start w-full">
                      <div className="flex items-center gap-1.5">
                        {aa.code && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            {aa.code}
                          </span>
                        )}
                        {aa.action_type && (
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {aa.action_type === 102 ? 'sales' : aa.action_type === 103 ? 'purchases' : `type ${aa.action_type}`}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {aa.description}
                      </span>
                    </div>
                  ) : opt.label
                }}
              />
            </div>

            {/* Analytic Parts — full-width row below Deal Name + Account Assignment */}
            {!clientId ? (
              <div className="col-span-2 flex flex-col gap-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Analytic Parts</span>
                <span className="text-sm text-amber-600 italic h-9 flex items-center">Select a client first</span>
              </div>
            ) : isLoadingAnalyticParts ? (
              <div className="col-span-2 flex flex-col gap-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Analytic Parts</span>
                <span className="text-sm text-gray-400 italic h-9 flex items-center">Loading...</span>
              </div>
            ) : analyticPartDefinitions.length > 0 ? (
              <div className="col-span-2 flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Analytic Parts</span>
                  {hasCombinations && (
                    <button
                      onClick={() => setIsLocked(!isLocked)}
                      className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      title={isLocked ? "Заключени комбинации" : "Свободен избор"}
                    >
                      {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {analyticPartDefinitions.map((definition) => (
                    <React.Fragment key={`group-${definition.account_code}-${definition.analytic_part_id}`}>
                      {renderAnalyticPartGroup(definition)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : selectedAccAssignment ? (
              <div className="col-span-2 flex flex-col gap-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Analytic Parts</span>
                <span className="text-sm text-gray-400 italic h-9 flex items-center">No analytic parts</span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </TooltipProvider>
  )
}

